import type { GlobalFormValidationError } from "@tanstack/react-form"

/**
 * Wrap a message for `form.setErrorMap`.
 *
 * TanStack Form v1 types each errorMap slot from the matching validator's
 * return type, so a bare string only type-checks on a slot whose validator
 * happens to return one. Mutation failures have no validator behind them, and
 * the slot they are reported on is arbitrary. `GlobalFormValidationError` is
 * the escape hatch v1 provides for exactly that: an error attributed to the
 * form rather than to a field, accepted on every slot.
 *
 * Beware the side effect, which v0's bare-string form did not have:
 * `setErrorMap` walks *every* registered field and writes `fields[name]` into
 * that field's matching slot. A name absent from `fields` is therefore
 * cleared, not left alone -- so a field error set just before this call
 * disappears. Pass it here rather than reaching for `setFieldMeta` alongside.
 */
export function formError(
  message: string,
  fields: Record<string, string> = {},
): GlobalFormValidationError<unknown> {
  return { form: message, fields }
}

/**
 * Pull the displayable text out of one entry of an errors array.
 *
 * v0 guaranteed strings; v1 does not. A validator that returns a string still
 * yields one, and core unwraps `formError`'s object to its `form` string before
 * it reaches `form.state.errors`. But a Standard Schema validator -- which is
 * how zod attaches now that v1 has absorbed the adapter -- yields issue
 * *objects*, and those carry the message under `message`. Rendering the array
 * directly would print "[object Object]" for those, which is what the removed
 * zod adapter used to spare us.
 *
 * Anything with no message to show is dropped rather than rendered.
 */
export function formErrorMessage(error: unknown): string | null {
  if (typeof error === "string") {
    return error
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const { message } = error as { message?: unknown }
    return typeof message === "string" ? message : null
  }
  return null
}
